import { UAParser } from 'ua-parser-js'

// These values correspond to deviceType returned from Facade
export enum DeviceType {
  Desktop = 'desktop',
  Mobile = 'mobile',
  Tablet = 'tablet',
  Other = 'other',
  Unknown = 'unknown',
}

export enum BrowserGroup {
  Chrome = 'chrome',
  Firefox = 'firefox',
  Safari = 'safari',
  IE = 'internet explorer',
  Edge = 'microsoft edge',
  Other = 'other',
  Unknown = 'unknown',
}

export enum DesktopOsGroup {
  Mac = 'mac',
  Windows = 'windows',
}

export enum MobileOsGroup {
  iOS = 'ios',
  Android = 'android',
}

export enum UnknownOsGroup {
  Other = 'other',
  Unknown = 'unknown',
}

export type OsGroup = DesktopOsGroup | MobileOsGroup | UnknownOsGroup

export interface ClientCapabilities {
  deviceType: DeviceType
  osGroup: OsGroup
  os: string | 'unknown'
  browserGroup: BrowserGroup
  browser: string | 'unknown'
  browserVersion: string | 'unknown' // full version string or "unknown"
  majorVersion: number // -1 in case of "unknown" version
}

const convertToOsGroup = (os?: string): OsGroup => {
  if (!os) {
    return UnknownOsGroup.Unknown
  }

  const osLower = os.toLowerCase()

  // Desktop OS - Mac
  if (osLower === 'mac os') {
    return DesktopOsGroup.Mac
  }

  // Desktop OS - Windows
  if (osLower === 'windows') {
    return DesktopOsGroup.Windows
  }

  // Mobile OS - iOS
  if (osLower === 'ios' || osLower === 'watchos') {
    return MobileOsGroup.iOS
  }

  // Mobile OS - Android
  if (osLower === 'android' || osLower === 'android-x86') {
    return MobileOsGroup.Android
  }

  // Fallback for any other OS
  return UnknownOsGroup.Other
}

const convertToKnownBrowserGroup = (browser?: string): BrowserGroup => {
  if (!browser) {
    return BrowserGroup.Unknown
  }

  const browserLower = browser.toLowerCase()

  // Chrome and Chrome-based browsers
  if (
    browserLower === 'chrome' ||
    browserLower === 'chrome headless' ||
    browserLower === 'chrome webview' ||
    browserLower === 'chromium'
  ) {
    return BrowserGroup.Chrome
  }

  // Firefox and Firefox-based browsers
  if (
    browserLower === 'firefox' ||
    browserLower === 'firefox focus' ||
    browserLower === 'firefox reality' ||
    browserLower === 'fennec' ||
    browserLower === 'firebird'
  ) {
    return BrowserGroup.Firefox
  }

  // Safari browsers only
  if (browserLower === 'safari' || browserLower === 'mobile safari') {
    return BrowserGroup.Safari
  }

  // Internet Explorer
  if (
    browserLower === 'ie' ||
    browserLower === 'iemobile' ||
    browserLower === 'internet explorer'
  ) {
    return BrowserGroup.IE
  }

  // Microsoft Edge (modern Chromium-based)
  if (browserLower === 'edge') {
    return BrowserGroup.Edge
  }

  // All other browsers
  return BrowserGroup.Other
}

const convertToDeviceType = (device?: string): DeviceType => {
  if (!device) {
    // If no device type is provided, assume desktop
    // UAParser doesn't report 'desktop' directly, so undefined typically means desktop
    return DeviceType.Desktop
  }

  const deviceLower = device.toLowerCase()

  // Direct mappings from UAParser device types
  if (deviceLower === 'mobile') {
    return DeviceType.Mobile
  }

  if (deviceLower === 'tablet') {
    return DeviceType.Tablet
  }

  // Map other device types to 'Other'
  if (
    deviceLower === 'console' ||
    deviceLower === 'smarttv' ||
    deviceLower === 'wearable' ||
    deviceLower === 'embedded'
  ) {
    return DeviceType.Other
  }

  // Fallback - if device type is unknown or unrecognized
  return DeviceType.Unknown
}

// /**
//  * This is mock for DEV env
//  * It tries to return the same ClientCapabilities object as we receive from Facade
//  */
export function getClientCapabilities(userAgent: string | undefined): ClientCapabilities {
  const parser = new UAParser(userAgent)
  const { version: browserVersion, name: browserName } = parser.getBrowser()
  const { version: osVersion, name: osName } = parser.getOS()
  const { type: deviceType } = parser.getDevice()

  return {
    deviceType: convertToDeviceType(deviceType),
    osGroup: convertToOsGroup(osName),
    os: osName ? `${osName} ${osVersion}` : 'unknown',
    browserGroup: convertToKnownBrowserGroup(browserName),
    browser: browserName || 'unknown',
    browserVersion: browserVersion || 'unknown',
    majorVersion: Number.parseInt(browserVersion || '-1'),
  }
}
